import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n';
import { useAlignedLabels } from '../lib/alignLabels';

interface Props {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: number;
}

/**
 * Centred card modal. Escape or the close button closes it. A click on the
 * backdrop does not: most of these hold a form, and a stray click beside the
 * card threw away everything typed into it.
 *
 * Rendered through a portal to <body>, not in place. The backdrop is
 * position:fixed, and a fixed element is positioned against the nearest
 * ancestor with a transform/filter/backdrop-filter rather than the viewport —
 * the sticky header has a backdrop-filter, so a modal opened from the header
 * avatar was being centred on the header and clipped off-screen.
 */
export function Modal({ title, onClose, children, maxWidth = 460 }: Props) {
  const { t, lang } = useT();
  const backdrop = useRef<HTMLDivElement>(null);
  // Every form in a modal: fields sharing a row keep their boxes level.
  const card = useRef<HTMLDivElement>(null);
  useAlignedLabels(card, [lang]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A confirm opened over another dialog: Escape closes only the top one.
      const open = document.querySelectorAll('.modal-backdrop');
      if (open[open.length - 1] === backdrop.current) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    // Clicks still stop here: React bubbles portal events up to the opener,
    // which may itself be clickable.
    <div className="modal-backdrop" ref={backdrop} onClick={(e) => e.stopPropagation()}>
      <div
        ref={card}
        className="card modal-card"
        style={{ maxWidth }}
        role="dialog"
        aria-modal="true"
      >
        <div className="row between">
          <h2>{title}</h2>
          <button className="btn ghost sm" onClick={onClose} aria-label={t('close')}>
            <i className="ri-close-line" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
